import { auth, functionsEu, httpsCallable } from '../firebase';

// Mirror of STUDENT_DOMAINS in functions/index.js — used for UI hints only,
// actual domain validation is authoritative on the server.
export const KNOWN_UNIVERSITY_DOMAINS: Record<string, string> = {
  'tu-dresden.de': 'TU Dresden',
  'mailbox.tu-dresden.de': 'TU Dresden',
  'htw-dresden.de': 'HTW Dresden',
  'student.htw-dresden.de': 'HTW Dresden',
  'hfbk-dresden.de': 'HfBK Dresden',
  'palucca.eu': 'Palucca University of Dance Dresden',
  'hfmdd.de': 'Hochschule für Musik Dresden',
  'kirchenmusik-dresden.de': 'Hochschule für Kirchenmusik Dresden',
  'ehs-dresden.de': 'Evangelische Hochschule Dresden',
  'fh-dresden.eu': 'Fachhochschule Dresden',
  'di-uni.de': 'Dresden International University',
  'srh-university.de': 'SRH Campus Dresden',
  'ba-dresden.de': 'Berufsakademie Sachsen - Dresden',
  'ba-sachsen.de': 'Berufsakademie Sachsen',
};

export type StudentVerificationStatus =
  | 'unverified'
  | 'email_verification_sent'
  | 'pending_admin_review'
  | 'approved'
  | 'denied'
  | 'pending'
  | 'email_sent'
  | 'failed'
  | 'rejected'
  | 'expired';

export interface StudentVerificationRequest {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  submittedEmail: string;
  emailDomain: string;
  status: StudentVerificationStatus;
  createdAt: unknown;
  updatedAt: unknown;
  verifiedEmailOwnership: boolean;
  studentUniversity?: string;
}

export function extractDomain(email: string): string {
  const normalized = email.trim().toLowerCase();
  const atIndex = normalized.lastIndexOf('@');
  if (atIndex < 0) return '';
  return normalized.slice(atIndex + 1).replace(/\.+$/, '');
}

export function isKnownDomain(email: string): boolean {
  return extractDomain(email) in KNOWN_UNIVERSITY_DOMAINS;
}

export function universityForEmail(email: string): string {
  return KNOWN_UNIVERSITY_DOMAINS[extractDomain(email)] ?? '';
}

/**
 * Calls the sendStudentVerificationEmail Cloud Function (HTTP endpoint).
 * The function sends a verification link to the submitted email, creates a
 * studentVerificationRequests document, and updates users/{uid}.
 */
export async function callSendVerificationEmail(email: string): Promise<void> {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('You must be signed in.');
  const idToken = await currentUser.getIdToken();

  const url = 'https://europe-west3-triptribe-8babd.cloudfunctions.net/sendStudentVerificationEmailEu';
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ data: { email } }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(
      (body as any)?.error || `Verification request failed (${response.status}).`,
    );
  }
}

/**
 * Callable Cloud Function — admin approves a pending verification request.
 * Accepting a reviewed request saves the domain to approvedStudentDomains.
 */
export async function callApproveVerificationRequest(
  requestId: string,
  saveDomain = false,
): Promise<void> {
  const fn = httpsCallable<{ requestId: string; saveDomain: boolean }, { success: boolean }>(
    functionsEu,
    'approveStudentVerificationRequestEu',
  );
  await fn({ requestId, saveDomain });
}

/**
 * Callable Cloud Function — admin denies a pending verification request.
 */
export async function callDenyVerificationRequest(requestId: string): Promise<void> {
  const fn = httpsCallable<{ requestId: string }, { success: boolean }>(
    functionsEu,
    'denyStudentVerificationRequestEu',
  );
  await fn({ requestId });
}

/**
 * Verifies the email ownership token from the deep-link, via a raw fetch to
 * the callable function's HTTP endpoint (same protocol httpsCallable uses
 * under the hood) rather than the `httpsCallable` SDK helper itself.
 *
 * httpsCallable was observed failing fast (~600ms) with a generic
 * `functions/internal` error on Android emulators, with the request never
 * reaching the Cloud Run service (confirmed via server-side logs) — while
 * this exact endpoint responds correctly to a plain fetch from any other
 * network. callSendVerificationEmail above already uses raw fetch for the
 * same reason; mirroring that here sidesteps whatever RN/emulator-specific
 * transport issue affects the SDK's callable path.
 *
 * Invoked by StudentVerificationCompleteScreen after the user taps the link
 * in their email. Public callable — works even if the user is not signed
 * in, though in practice they always are.
 */
export async function callVerifyStudentToken(token: string): Promise<{
  status: string;
  university?: string;
  message?: string;
}> {
  const idToken = await auth.currentUser?.getIdToken().catch(() => undefined);

  const url = 'https://europe-west3-triptribe-8babd.cloudfunctions.net/verifyStudentEmailTokenEu';
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ data: { token } }),
  });

  const body: any = await response.json().catch(() => ({}));

  if (!response.ok || body?.error) {
    const status: string = body?.error?.status || '';
    const code = status ? `functions/${status.toLowerCase().replace(/_/g, '-')}` : 'functions/internal';
    const error: any = new Error(body?.error?.message || `Verification failed (${response.status}).`);
    error.code = code;
    throw error;
  }

  return body.result;
}
